<template>
  <div class="warehouse main-top-margin">
    <v-tabs
      @change="changeTab"
      v-model="activeTab"
      background-color="transparent"
      color="#26262A"
      slider-color="#26262A"
      light
      class="tabs-primary"
      slider-size="3"
    >
      <v-tab :ripple="false">
        <lang t="WAREHOUSE-TAB-SETTINGS"></lang>
      </v-tab>


      <v-tab :ripple="false">
        <lang t="WAREHOUSE-TAB-TEACH-IN"></lang>
      </v-tab>

      <v-tab :ripple="false">
        <lang t="WAREHOUSE-TAB-INTEGRATIONS"></lang>
      </v-tab>

      <v-tab :ripple="false" v-if="currentUser.role == 'adm' || currentUser.role == 'su'">
        <lang t="WAREHOUSE-TAB-ARTICLES"></lang>
      </v-tab>

      <v-tab :ripple="false">
        <lang t="WAREHOUSE-TAB-MEASUREMENTS"></lang>
      </v-tab>
      <v-tab :ripple="false">
        <lang t="WAREHOUSE-TAB-VALUE_CHAIN"></lang>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="mt-6">
      <!-- tab DATA -->
      <v-tab-item class="py-6 px-0" style="overflow: hidden;">
        <div v-if="isLoading" class="text-center ma-12">
          <v-progress-circular size="48" width="4" indeterminate color="grey lighten-2"></v-progress-circular>
        </div>

        <template v-else>
          <v-expansion-panels focusable flat multiple v-model="expansionPanel">
            <!-- ProductionSystems -->
            <SimpleExpensionPanel :title="$t('WAREHOUSE-LABEL-PRODUCTION_SYSTEMS')" :selectedPanels="expansionPanel">
              <SubHeader :text="$t('WAREHOUSE-LABEL-PRODUCTION_SYSTEMS')" />
              <v-chip-group column multiple active-class="themeColor" v-model="warehouse.productionsystems">
                <simple-chip
                  v-for="ps in productionSystems"
                  :value="ps.id"
                  :key="ps.id"
                  :name="ps.name"
                  :small="true"
                  :selectedItems="warehouse.productionsystems"
                />
              </v-chip-group>
            </SimpleExpensionPanel>

            <!-- LHMs -->
            <SimpleExpensionPanel :index="1" :title="$t('WAREHOUSE-LABEL-LHMS-TRAYS')" :selectedPanels="expansionPanel">
              <SubHeader :text="$t('WAREHOUSE-LABEL-LHMS')" />
              <v-chip-group column multiple active-class="themeColor" v-model="warehouse.lhmHelper">
                <simple-chip
                  :small="true"
                  v-for="lc in loadCarriers"
                  :value="lc.id"
                  :key="lc.id"
                  :name="lc.name"
                  :selectedItems="warehouse.lhmHelper"
                />
              </v-chip-group>
              <v-divider class="my-8" style="border: none;"></v-divider>

              <!-- TRAYS -->
              <SubHeader :text="$t('WAREHOUSE-LABEL-TRAYS')" />
              <div
                style=" max-width: 768px; background-color: #fff;"
                class="border-gray-thin mb-2 pa-3 rounded"
                v-if="warehouse.trayHelper.length > 0"
              >
                <v-row class="trays__row text-body-2" align="center" v-for="(tray, index) in warehouse.trayHelper" :key="tray">
                  <v-col cols="1">
                    <v-img
                      max-width="50"
                      max-height="28"
                      contain
                      :src="'https://knapp.jumptoday.group/assets/' + tray.image"
                      v-if="tray.image != 'none'"
                    >
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular size="16" width="2" indeterminate color="grey lighten-2"></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                  </v-col>

                  <v-col cols="3">
                    <span class="text-xs">
                      {{ tray.name }}
                    </span>
                  </v-col>

                  <v-col>
                    {{ tray.material }}
                  </v-col>

                  <v-col>
                    {{ tray.length_mm }} x {{ tray.width_mm }} x
                    {{ tray.height_mm }}
                  </v-col>

                  <v-col cols="2" style="text-align:right;">
                    <v-btn x-small :title="$t('GLOBAL-MESSAGE-EDIT')" color="grey" icon @click="trayEdit(tray, index)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn x-small :title="$t('GLOBAL-MESSAGE-DELETE')" color="grey" icon @click="trayDelete(index)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <v-btn small depressed color="#fff" class="mt-2" @click="trayNew()">
                <v-icon small>mdi-plus</v-icon>
                <lang t="WAREHOUSE-BTN-ADD_TRAY"></lang>
              </v-btn>
            </SimpleExpensionPanel>

            <!-- Danger Symbols -->
            <SimpleExpensionPanel :index="2" :title="$t('WAREHOUSE-LABEL-DANGER_SYMBOLS')" :selectedPanels="expansionPanel">
              <SubHeader :text="$t('WAREHOUSE-LABEL-DANGER_SYMBOLS')" />
              <v-chip-group column multiple active-class="themeColor" v-model="warehouse._symbols">
                <simple-chip
                  v-for="symbol in warehouseSymbols"
                  :value="symbol.id"
                  small
                  :key="symbol.id"
                  :name="attributeName(symbol)"
                  :img="symbol.imageUrl === null ? undefined: symbol.imageUrl"
                  :selectedItems="warehouse._symbols"
                />
              </v-chip-group>
            </SimpleExpensionPanel>

            <!-- ITEM TYPES -->
            <SimpleExpensionPanel :index="3" :title="$t('WAREHOUSE-LABEL-ITEM-TYPES')" :selectedPanels="expansionPanel">
              <div v-for="itemType in ['case', 'pack', 'unit']" :key="itemType">
                <SubHeader :text="$t('WAREHOUSE-LABEL-BUNDLETYPES-' + itemType.toUpperCase())" />
                <v-chip-group column multiple active-class="themeColor" v-model="warehouse.bundletypes">
                  <simple-chip
                    v-for="bundle in bundleTypes.filter((_) => _.level == itemType)"
                    :key="bundle.id"
                    :value="bundle.id"
                    :name="bundle.displayName"
                    :img="bundle.image ? $config.cdn + bundle.image : ''"
                    :selectedItems="warehouse.bundletypes"
                  />
                </v-chip-group>
              </div>
            </SimpleExpensionPanel>

            <!-- Bundle complete rules -->
            <SimpleExpensionPanel :index="4" :title="$t('WAREHOUSE-LABEL-BUNDLE_COMPLETE_RULES')" :selectedPanels="expansionPanel">
              <SubHeader :text="$t('WAREHOUSE-LABEL-BUNDLE_COMPLETE_RULES')" />

              <div class="mb-2">
                <v-data-table
                  hide-default-footer
                  :headers="bundleCompleteRulesHeaders"
                  :items="bundleCompleteRules"
                  :items-per-page="25"
                  style="max-width: 768px;"
                  class="mb-8 bundle-complete-tbl elevation-0"
                >
                  <template v-slot:item.rule="{ item }">
                    <lang :t="item.rule" />
                  </template>

                  <template v-slot:item.case="{ item }">
                    <template v-if="item.case.visible">
                      <checkbox :value="item.case.checked" @updatedValue="item.case.checked = $event" />
                    </template>
                  </template>

                  <template v-slot:item.pack="{ item }">
                    <template v-if="item.pack.visible">
                      <checkbox :value="item.pack.checked" @updatedValue="item.pack.checked = $event" />
                    </template>
                  </template>

                  <template v-slot:item.unit="{ item }">
                    <template v-if="item.unit.visible">
                      <checkbox :value="item.unit.checked" @updatedValue="item.unit.checked = $event" />
                    </template>
                  </template>
                </v-data-table>
              </div>
            </SimpleExpensionPanel>

            <!-- Configurations -->
            <SimpleExpensionPanel :index="5" :title="$t('WAREHOUSE-LABEL-CONFIGURATIONS')" :selectedPanels="expansionPanel">
              <SubHeader :text="$t('WAREHOUSE-LABEL-CONFIGURATIONS')" />

              <!-- BUNDLE GROUP TOLERANCE -->
              <v-text-field v-model="warehouse._settings.bundlegroupTolerance" :label="$t('WAREHOUSE-LABEL-BUNDLEGROUP_TOLERANCE')"></v-text-field>
              <v-divider class="my-8" style="border: none;"></v-divider>

              <!-- CREATE ITEM SWITCHER -->
              <v-switch v-model="warehouse._settings.allowArticleNoReuse" :label="$t('WAREHOUSE-LABEL-ALLOW_ARTICLE-NO-REUSE')"></v-switch>
              
              <v-btn  color="error" v-if="currentUser.role == 'su' && (envs.isDev || envs.isLocal || envs.isStage)" @click="showConfirmClearWarehouseDialog = true" elevation="0">{{$t('CLEAR-WAREHOUSE-LABEL')}}</v-btn>
              <v-dialog v-model="showConfirmClearWarehouseDialog" :persistent="false" max-width="600">
                  <v-card>
                    <v-card-title>
                      <span class="headline">{{$t('CLEAR-WAREHOUSE-CONFIRMATION-TITLE')}}</span>
                    </v-card-title>
                    <v-card-text>
                      <p>{{ $t('CLEAR-WAREHOUSE-CONFIRMATION-TEXT') }}</p>
                        <BoxItem
                          :title="$t('CLEAR-WAREHOUSE-CONFIRMATION-NAME')"
                          :value="clearWarehouseName"
                          showRequiredSymbol
                          required
                          :rules="[rules.required]"
                          @updateValue="clearWarehouseName = $event"
                        />
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn text @click="showConfirmClearWarehouseDialog = false"><lang t="GLOBAL-BTN-CANCEL"/></v-btn>
                      <v-btn @click="clearWarehouseTrigger" class="general-action-btn" :disabled="!clearWarehouseName.length" elevation="0"  rounded ><lang t="CLEAR-WAREHOUSE-BTN"/></v-btn>
                    </v-card-actions>
                    
                  </v-card>
                </v-dialog>
            </SimpleExpensionPanel>
          </v-expansion-panels>

          <v-btn color="secondary" :loading="isSaving" fixed bottom right @click="save()" style="z-index:999;">
            <small> <lang t="GLOBAL-BTN-SAVE"/></small>
          </v-btn>
        </template>
      </v-tab-item>

      <!-- tab Teach-In Screens -->
      <v-tab-item class="py-6 px-0">
        <box maxWidth="1500px" v-if="teachinGlobalSetting && teachinGlobalSetting.length >0">
            <SubHeader :text="$t('WAREHOUSE-TEACH-IN-SETTINGS')" />
            <TeachinSettings :settings="teachinGlobalSetting" @syncSettings="teachinGlobalSetting = $event"
            />
        </box>
        <box maxWidth="1500px" class="mt-3">
            <SubHeader :text="$t('WAREHOUSE-TEACH-IN-SECTIONS')" />
            <TeachinSectionList :sections="teachInSections" @active="teachinActiveSection = $event" @syncSection="syncTeachInSection"
            @syncSections="syncTeachinSections"/>
            <TeachinSection :section="teachinActiveSection" @syncSection="syncTeachInSection"/>
        </box>
        <v-btn color="secondary" :loading="isSaving" fixed bottom right @click="save()" style="z-index:999;">
             <small> <lang t="GLOBAL-BTN-SAVE"/></small>
        </v-btn>
      </v-tab-item>

      <!-- tab INTEGRATIONS -->
      <v-tab-item class="pa-6">
        <box>
          <SubHeader :text="$t('WAREHOUSE-LABEL-MEDIASERVER')" />
          <v-row dense>
            <v-col cols="12 px-2">
              <v-row dense>
                <v-col cols="6">
                  <!-- Protocol-->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-PROTOCOL')"
                    :value="warehouse.media_server_account.protocol"
                    :items="protocols"
                    @updateValue="warehouse.media_server_account.protocol = $event"
                  />
                </v-col>
                <v-col cols="6">
                  <!-- remotePathPrefix -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_REMOTE_PATH_PREFIX')"
                    :value="warehouse.media_server_account.remotePathPrefix"
                    @updateValue="warehouse.media_server_account.remotePathPrefix = $event"
                  />
                </v-col>

                <v-col cols="6">
                  <!-- Host-->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_HOST')"
                    :value="warehouse.media_server_account.host"
                    @updateValue="warehouse.media_server_account.host = $event"
                  />
                </v-col>

                <v-col cols="6">
                  <!-- Port -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_PORT')"
                    :value="warehouse.media_server_account.port"
                    @updateValue="warehouse.media_server_account.port = $event"
                  />
                </v-col>

                <v-col cols="6">
                  <!-- User -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_USERNAME')"
                    :value="warehouse.media_server_account.username"
                    @updateValue="warehouse.media_server_account.username = $event"
                  />
                </v-col>

                <v-col cols="6">
                  <!-- Password -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_PASSWORD')"
                    :value="warehouse.media_server_account.password"
                    type="password"
                    @updateValue="warehouse.media_server_account.password = $event"
                  />
                </v-col>

                <v-col cols="12">
                  <!-- Remote Path -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-REMOTE_PATH')"
                    :value="warehouse.media_server_account.remote_path"
                    @updateValue="warehouse.media_server_account.remote_path = $event"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </box>

        <v-divider class="my-8" style="border: none;"></v-divider>

        <!-- KISOFT  -->
        <box>
          <v-row dense>
            <v-col cols="12 px-2">
              <v-row>
                <v-col cols="12">
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-HAS_KISOFT')"
                    :smallLabel="false"
                    :value="warehouse.kisoft"
                    type="switch"
                    @updateValue="warehouse.kisoft = $event"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12 px-2" v-if="warehouse.kisoft == true">
              <v-row v-if="warehouse.kisoft">
                <v-col cols="7">
                  <!-- HOST -->
                  <BoxItem :title="$t('WAREHOUSE-LABEL-HOST')" :value="warehouse.host" @updateValue="warehouse.host = $event" />
                </v-col>
                <v-col cols="5">
                  <!-- Protocol-->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-KISOFT_MODE')"
                    :value="warehouse.kisoft_mode"
                    :items="kisoftModes"
                    @updateValue="warehouse.kisoft_mode = $event"
                  />
                </v-col>
              </v-row>
              <v-row v-if="warehouse.kisoft && warehouse.kisoft_mode == 'ftp'">
                <v-col cols="7">
                  <!-- Host-->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_HOST')"
                    :value="warehouse.kisoft_account.host"
                    @updateValue="warehouse.kisoft_account.host = $event"
                  />
                </v-col>
                <v-col cols="5">
                  <!-- Remote Path IN -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-REMOTE_PATH_IN')"
                    :value="warehouse.kisoft_account.remote_path_in"
                    @updateValue="warehouse.kisoft_account.remote_path_in = $event"
                  />
                </v-col>
                <v-col cols="3">
                  <!-- Port -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_PORT')"
                    :value="warehouse.kisoft_account.port"
                    @updateValue="warehouse.kisoft_account.port = $event"
                  />
                </v-col>
                <v-col cols="4">
                  <!-- User -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_USERNAME')"
                    :value="warehouse.kisoft_account.username"
                    @updateValue="warehouse.kisoft_account.username = $event"
                  />
                </v-col>
                <v-col cols="5">
                  <!-- Password -->
                  <BoxItem
                    type="password"
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_PASSWORD')"
                    :value="warehouse.kisoft_account.password"
                    @updateValue="warehouse.kisoft_account.password = $event"
                  />
                </v-col>
                <v-col cols="12">
                  <!-- Remote Path OUT -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-REMOTE_PATH_OUT')"
                    :value="warehouse.kisoft_account.remote_path_out"
                    @updateValue="warehouse.kisoft_account.remote_path_out = $event"
                  />
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col cols="12">
                  <BoxItem
                    v-if="warehouse.kisoft"
                    :title="$t('WAREHOUSE-LABEL-KISOFT_URL')"
                    :value="warehouse.kisoft_url"
                    @updateValue="warehouse.kisoft_url = $event"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="6">
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-BUNDLE_SURFACE_ELASTICITY')"
                    :value="warehouse._settings.bundleSurfaceElasticity"
                    type="switch"
                    @updateValue="warehouse._settings.bundleSurfaceElasticity = $event"
                  />
                </v-col>
                <v-col cols="6">
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-NESTING')"
                    :value="warehouse._settings.nesting"
                    type="switch"
                    @updateValue="warehouse._settings.nesting = $event"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </box>

        <v-divider class="my-8" style="border: none;"></v-divider>

        <!-- MULTISCANS -->
        <box>
          <v-row dense>
            <v-col cols="12 px-2">
              <SubHeader :text="$t('WAREHOUSE-LABEL-MULTISCANS')" />
            </v-col>
            <v-col cols="11">
              <v-row dense v-if="multiscans && multiscans.length > 0">
                <v-col v-for="(scan, index) in multiscans" :key="index" cols="6">
                  <multiscanBox
                    :item="scan"
                    :index="index"
                    :title="$t('WAREHOUSE-LABEL-MULTISCANS')"
                    @edit="scanEdit($event.scan, $event.index)"
                    @deleteScan="scanDelete($event)"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="1 d-flex justify-end align-end">
              <v-btn small text @click="scanNew()">
                <v-icon color="#404046">mdi-plus</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </box>

        <v-divider class="my-8" style="border: none;"></v-divider>

        <box>
          <v-row dense>
            <v-col cols="12 px-2">
              <v-row>
                <v-col cols="12">
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-IRIIS')"
                    :value="warehouse.iriis"
                    :smallLabel="false"
                    type="switch"
                    @updateValue="warehouse.iriis = $event"
                  />
                </v-col>
              </v-row>
              <v-row dense v-if="warehouse.iriis">
                <v-col cols="4">
                  <!-- Protocol -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-PROTOCOL')"
                    :value="warehouse.iriis_account.protocol"
                    :items="protocols"
                    @updateValue="warehouse.iriis_account.protocol = $event"
                  />
                </v-col>
                <v-col cols="5">
                  <!-- Host-->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_HOST')"
                    :value="warehouse.iriis_account.host"
                    @updateValue="warehouse.iriis_account.host = $event"
                  />
                </v-col>
                <v-col cols="3">
                  <!-- Port -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_PORT')"
                    :value="warehouse.iriis_account.port"
                    @updateValue="warehouse.iriis_account.port = $event"
                  />
                </v-col>
                <v-col cols="4">
                  <!-- Remote Path -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-REMOTE_PATH')"
                    :value="warehouse.iriis_account.remote_path"
                    @updateValue="warehouse.iriis_account.remote_path = $event"
                  />
                </v-col>
                <v-col cols="5">
                  <!-- User -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_USERNAME')"
                    :value="warehouse.iriis_account.username"
                    @updateValue="warehouse.iriis_account.username = $event"
                  />
                </v-col>
                <v-col cols="3">
                  <!-- Password -->
                  <BoxItem
                    :title="$t('WAREHOUSE-LABEL-MEDIASERVER_PASSWORD')"
                    :value="warehouse.iriis_account.password"
                    type="password"
                    @updateValue="warehouse.iriis_account.password = $event"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </box>

        <v-divider class="my-8" style="border: none;"></v-divider>


        <v-btn color="secondary" :loading="isSaving" fixed bottom right @click="save()" style="z-index:999;">
          <small><lang t="GLOBAL-BTN-SAVE"/></small>
        </v-btn>
      </v-tab-item>
      <!-- tab ITEMS -->
      <v-tab-item class="pa-6" v-if="!isLoading">

        <item-index :largeMargin="false" :warehouseId="warehouse.id" :warehouse="warehouse"></item-index>
      </v-tab-item>

      <!-- tab MEASUREMENTS -->
      <v-tab-item class="pa-6" v-if="!isLoading">
        <tab-measurements :warehouseId="warehouse.id"></tab-measurements>
      </v-tab-item>

      <!-- tab VALUE CHAIN -->
      <v-tab-item class="pa-6" v-if="!isLoading">
        <tab-value-chain :warehouseId="warehouse.id" />
      </v-tab-item>
    </v-tabs-items>

 

    <v-dialog v-model="dialog.show" :max-width="dialog.maxWidth" :persistent="false" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-card-title>
          <span class="headline"><lang :t="dialog.title"></lang></span>
        </v-card-title>

        <v-card-text>
          <!-- TRAY -->
          <template v-if="dialog.type == 'tray'">
            <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-NAME')" v-model="editedTray.name"></v-text-field>

            <v-row dense>
              <v-col cols="12">
                <v-select
                  :label="$t('WAREHOUSE-TRAY-LABEL-MATERIAL')"
                  v-model="editedTray.material"
                  :items="materials"
                  item-text="name"
                  item-value="value"
                ></v-select>
              </v-col>

              <v-col cols="4">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-WIDTH')" v-model="editedTray.width_mm"></v-text-field>
              </v-col>

              <v-col cols="4">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-LENGTH')" v-model="editedTray.length_mm"></v-text-field>
              </v-col>

              <v-col cols="4">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-HEIGHT')" v-model="editedTray.height_mm"></v-text-field>
              </v-col>

              <v-col cols="6">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-MAX_HEIGHT')" v-model="editedTray.settings.maxHeight"></v-text-field>
              </v-col>

              <v-col cols="6">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-MAX_WEIGHT_SINGLE')" v-model="editedTray.settings.maxWeightSingle"></v-text-field>
              </v-col>

              <v-col cols="6">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-MAX_WEIGHT_DOUBLE')" v-model="editedTray.settings.maxWeightDouble"></v-text-field>
              </v-col>

              <v-col cols="6">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-MAX_WIDTH_ROTATED')" v-model="editedTray.settings.maxWidthRotated"></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-MIN_GAPS')" v-model="editedTray.settings.minGaps"></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-MAX_QTY')" v-model="editedTray.max_quantity"></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field :label="$t('WAREHOUSE-TRAY-LABEL-KISOFT_ID')" v-model="editedTray.kisoftId"></v-text-field>
              </v-col>
            </v-row>

          </template>

          <!-- MULTISCAN -->
          <template v-else-if="dialog.type == 'scan'">
            <v-row dense>
              <v-col cols="12">
                <v-text-field :label="$t('WAREHOUSE-MULTISCAN-NAME')" v-model="editedScan.name"></v-text-field>
              </v-col>

              <v-col cols="4">
                <v-text-field :label="$t('WAREHOUSE-MULTISCAN-ADJUST_WIDTH')" v-model="editedScan.adjust_width"></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field :label="$t('WAREHOUSE-MULTISCAN-ADJUST_LENGTH')" v-model="editedScan.adjust_length"></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field :label="$t('WAREHOUSE-MULTISCAN-ADJUST_HEIGHT')" v-model="editedScan.adjust_height"></v-text-field>
              </v-col>

              <v-col cols="9">
                <v-text-field :label="$t('WAREHOUSE-MULTISCAN-HOST')" v-model="editedScan.host"></v-text-field>
              </v-col>

              <v-col cols="3">
                <v-text-field :label="$t('WAREHOUSE-MULTISCAN-PORT')" v-model="editedScan.port"></v-text-field>
              </v-col>
            </v-row>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn text @click="closeModal" :disabled="dialog.isSaving"><lang t="GLOBAL-BTN-CANCEL"></lang></v-btn>

          <template v-if="dialog.type == 'tray'">
            <v-btn elevation="0" light rounded color="secondary" :loading="dialog.isSaving" @click="traySave()"
              ><lang t="GLOBAL-BTN-SAVE"></lang
            ></v-btn>
          </template>

          <template v-else-if="dialog.type == 'scan'">
            <v-btn elevation="0" light rounded color="secondary" :loading="dialog.isSaving" @click="scanSave()"
              ><lang t="GLOBAL-BTN-SAVE"></lang
            ></v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ItemIndex from "../items/Index";
import tabMeasurements from "./tab-measurements.vue";
import tabValueChain from "./tab-value-chain.vue";
import SimpleExpensionPanel from "../../components/panels/SimpleExpensionPanel.vue";
import SimpleChip from "../../components/ui-elements/SimpleChip.vue";
import SubHeader from "../../components/ui-elements/SubHeader.vue";
import Checkbox from "../../components/ui-elements/Checkbox.vue";
import Box from "../../components/ui-elements/Box.vue";
import BoxItem from "../../components/ui-elements/BoxItem.vue";
import MultiscanBox from "../../components/multiscans/MultiscanBox.vue";
import { cloneDeep, isEqual } from "lodash";
import TeachinSection from './teachin/TeachinSection.vue';
import TeachinSectionList from './teachin/TeachinSectionList.vue';
import TeachinSettings from './teachin/TeachinSettings.vue';

export default {
  name: "Warehouse",
  beforeRouteEnter(to, from, next) {
    if (to.params && to.params.tab) {
      next((vm) => {
        if (vm.currentUser.role == "su") {
          vm.activeTab = 3;
        }
      });
    } else if (from.name == "item") {
      next((vm) => {
        vm.activeTab = 3;
      });
    } else {
      next();
    }
  },

  components: {
    ItemIndex,
    tabMeasurements,
    tabValueChain,
    SimpleExpensionPanel,
    SimpleChip,
    SubHeader,
    Checkbox,
    Box,
    BoxItem,
    MultiscanBox,
    TeachinSection,
    TeachinSectionList,
    TeachinSettings
  },

  data: () => ({
    isLoading: true,
    isSaving: false,
    teachinActiveSection: null,
    expansionPanel: [0],
    dialog: {
      show: false,
      title: "",
      type: null,
      isSaving: false,
      maxWidth: "480px",
    },
    showConfirmClearWarehouseDialog: false,
    clearWarehouseFormValid: false,
    clearWarehouseName: '',
    activeTab: 0,

    warehouse: {
      settings: {
        bundleCompleteRules: null,
        bundleSurfaceElasticity: false,
        nesting: false,
      },
      media_server_account: {
        protocol: null,
        remote_path: null,
        host: null,
        port: null,
        username: null,
        password: null,
      },
      genomix: {
        status: false,
        api_key: "",
        mode: "",
        attributes_settings: [],
        downlink: {
          status: false,
          api_keys: [],
        },
      },
      iriis_account: {
        protocol: null,
        remote_path: null,
        host: null,
        port: null,
        username: null,
        password: null,
      },
    },
    downlinkApiKeys: [],
    showGenomix: false,
    showGenomixDownlink: false,
    attributes: [],
    defaultAttributes: [],
    warehouseSymbols: [],
    productionSystems: null,
    loadcarriers: null,
    bundleTypes: null,
    multiscans: null,
    clients: null,

    bundleCompleteRules: [],

    bundleCompleteRulesHeaders: [
      {
        text: "RULE",
        value: "rule",
        align: "start",
        sortable: false,
      },
      {
        text: "CASE",
        value: "case",
        align: "center",
        sortable: false,
      },
      {
        text: "PACK",
        value: "pack",
        align: "center",
        sortable: false,
      },
      {
        text: "UNIT",
        value: "unit",
        align: "center",
        sortable: false,
      },
    ],

    editedIndex: -1,

    editedTray: {
      name: "",
      type: "tray",
      material: null,
      width_mm: null,
      length_mm: null,
      height_mm: null,
      settings: {
        maxHeight: null,
        maxWeightDouble: null,
        maxWeightSingle: null,
        maxWidthRotated: null,
        minGaps: null,
      },
      warehouse_id: null,
      kisoftId: null,
      image: "none",
    },

    defaultTray: {
      name: "",
      type: "tray",
      material: null,
      width_mm: null,
      length_mm: null,
      height_mm: null,
      settings: {
        maxHeight: null,
        maxWeightDouble: null,
        maxWidthRotated: null,
        maxWeightSingle: null,
        minGaps: null,
      },
      warehouse_id: null,
      kisoftId: null,
      image: "none",
    },

    editedScan: {
      name: null,
      host: null,
      port: null,
      adjust_width: null,
      adjust_height: null,
      adjust_length: null,
      warehouse_id: null,
    },
    defaultScan: {
      name: null,
      host: null,
      port: null,
      adjust_width: null,
      adjust_height: null,
      adjust_length: null,
      warehouse_id: null,
    },
    teachInSections: null,
    defaultTeachInSections: null,
    teachinGlobalSetting: null,
    defaultTeachinGlobalSetting: null,
    isTeachinChanged: false,

    protocols: [
      {
        text: "FTP",
        value: "ftp",
      },
      {
        text: "SFTP",
        value: "sftp",
      },
    ],

    kisoftModes: [
      {
        text: "Webservice",
        value: "webservice",
      },
      {
        text: "FTP",
        value: "ftp",
      },
    ],
  }),

  computed: {
    currentLangShortCode() {
      return this.$store.getters.langShortcode;
    },
    envs(){
      return this.$store.getters.envs;
    },
    genomixModes() {
      return [
        {
          value: "READ",
          text: this.$t("READ"),
        },
        {
          value: "WRITE",
          text: this.$t("WRITE"),
        },
        {
          value: "READ/WRITE",
          text: this.$t("READ/WRITE"),
        },
      ];
    },
    materials() {
      return [
        {
          name: this.$t("TRAY-MATERIAL-WOOD"),
          value: "wood",
        },
        {
          name: this.$t("TRAY-MATERIAL-POLYPROPYLEN"),
          value: "polypropylen",
        },
      ];
    },

    types() {
      return [
        {
          name: this.$t("TRAY-TYPE-TRAY"),
          value: "tray",
        },
        {
          name: this.$t("TRAY-TYPE-PALLET"),
          value: "pallet",
        },
      ];
    },
  },

  watch: {
    "dialog.show"(val) {
      val || this.closeModal();
    },

    "warehouse.kisoft_mode"(val) {
      if (val == "ftp") {
        if (!this.warehouse.kisoft_account) {
          this.warehouse.kisoft_account = {};
        }
      }
    },
    downlinkApiKeys: {
      handler() {
        this.warehouse.genomix.downlink.api_keys = cloneDeep(this.downlinkApiKeys);
      },
      deep: true,
    },
    attributes: {
      handler() {
        this.warehouse.genomix.attributes_settings = cloneDeep(this.attributes);
      },
      deep: true,
    },
    teachInSections:{
      handler(value){
        this.checkTeachinStatus();
      },
      deep: true
    },
    teachinGlobalSetting:{
      handler(value){
        this.checkTeachinStatus();
      },
      deep: true
    }
  },

  async created() {
    await this.loadAttributes();
    await this.getTeachInData();
    this.initialize();
  },

  mounted() {},

  methods: {
    attributeName(attribute){
      if(attribute.displayName && attribute.displayName[this.currentLangShortCode]){
        return attribute.displayName[this.currentLangShortCode];
      }
      return this.$i18n.t('ARTICLE-BUNDLE-ATTRIBUTE-' + attribute.name.toUpperCase());
    },
    
    async clearWarehouseTrigger(){
       const id = !this.warehouse || !this.warehouse.id? this.$route.params.id: this.warehouse.id;
      try{
        await this.axios.post(`warehouses/${id}/clear`,{confirm: this.clearWarehouseName});
        this.showConfirmClearWarehouseDialog = false;
           this.$store.dispatch("setSnackbar", {
            text: this.$t("WAREHOUSE-CLEARED"),
          });
      }catch(err){
        let status = err?.response?.data;
        
        switch(status){
          case "unconfirmed":{
            this.$store.dispatch("setSnackbar", {
              text: this.$i18n.t('WAREHOUSE-CLEAR-UNCONFIRM-ERROR'),
              type: "error",
            });
            break;
          }
          case 'Forbidden':{
           this.$store.dispatch("setSnackbar", {
              text: this.$i18n.t('WAREHOUSE-CLEAR-FORBIDDEN-ERROR'),
              type: "error",
            });
            break;
          }
          default:{
            console.error(err);
             this.$store.dispatch("setSnackbar", {
              text: "ERROR: "+err,
              type: "error",
            });
          }
        }
      }    
    },
    
    checkTeachinStatus(){
      let changedSections = false;
      if(this.teachInSections !== null && this.defaultTeachInSections === null){
        changedSections = true;
      }else if(this.teachInSections === null && this.defaultTeachInSections === null){
        changedSections = false;
      }else if(isEqual(this.teachInSections, this.defaultTeachInSections)){
        changedSections = false;
      }else{
        changedSections = true;
      }
      let changedSettings = false;
      if(this.teachinGlobalSetting !== null && this.defaultTeachinGlobalSetting === null){
        changedSettings = true;
      }else if(this.teachinGlobalSetting === null && this.defaultTeachinGlobalSetting === null){
        changedSettings = false;
      }else if(isEqual(this.teachinGlobalSetting, this.defaultTeachinGlobalSetting)){
        changedSettings = false;
      }else{
        changedSettings = true;
      }
       this.isTeachinChanged = changedSections || changedSettings;
    },
    syncTeachinSections(list){
      this.teachInSections = cloneDeep(list);
    },
    syncTeachInSection(section){
      const index = this.teachInSections.findIndex((el) => el.name === section.name);
      if(index === -1) throw Error('Cannot find teach in section');
      const teachInSections = cloneDeep(this.teachInSections);
      teachInSections[index] = cloneDeep(section);
      this.teachInSections = cloneDeep(teachInSections);
      this.teachinActiveSection = cloneDeep(section);
      this.checkTeachinStatus();
    },
    async getTeachInData(){
      try{
        const id = !this.warehouse || !this.warehouse.id? this.$route.params.id: this.warehouse.id;
        const response = await this.axios.get(`warehouses/${id}/teachin`);
        this.teachInSections = cloneDeep(response.data.sections);
        this.defaultTeachInSections = cloneDeep(response.data.sections);
        this.teachinGlobalSetting = cloneDeep(response.data.settings);
        this.defaultTeachinGlobalSetting = cloneDeep(response.data.settings);
      }catch(err){
        console.error(err);
      }
    },
    updateGenomixAttributeMode(attrIndex, mode) {
      this.$set(this.attributes[attrIndex], "mode", mode);
    },
    updateDownlinkApiKey(apiIndex, key) {
      this.$set(this.downlinkApiKeys[apiIndex], "key", key);
    },
    updateDownlinkApiMode(apiIndex, mode) {
      this.$set(this.downlinkApiKeys[apiIndex], "mode", mode);
    },
    insertNewGenomixDownlinkApi() {
      this.downlinkApiKeys.push({ key: "", mode: "" });
    },
    updateGenomixStatus(status) {
      this.warehouse.genomix.status = status;
      this.showGenomix = status;
    },

    updateGenomixDownlinkStatus(status) {
      this.warehouse.genomix.downlink.status = status;
      this.showGenomixDownlink = status;
    },
    async loadAttributes() {
      await this.axios.get("attributes").then((res) => {
        this.defaultAttributes = res.data;
        this.attributes = res.data
          .map((attr) => {
            let name = attr.displayName[this.$store.getters.langShortcode];
            if (!name) {
              name = attr.name;
            }
            return {
              id: attr.id,
              name,
              mode: "",
            };
          })
          .sort((a, b) => {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          });
      });
    },
    
    changeTab(tab) {},

    closeModal() {
      this.dialog.show = false;
      this.dialog.type = null;
      this.dialog.maxWidth = "480px";
      this.dialog.isSaving = false;

      this.editedIndex = -1;

      this.$nextTick(() => {
        this.editedTray = Object.assign({}, this.defaultTray);
        this.editedScan = Object.assign({}, this.defaultScan);
      });
    },

    initialize() {
      this.isLoading = true;

      this.editedTray.warehouse_id = this.defaultTray.warehouse_id = this.editedScan.warehouse_id = this.defaultScan.warehouse_id = this.$route.params.id;

      this.getMandatoryData().then(() => {
        this.axios.get("warehouses/" + this.$route.params.id).then((res) => {
          res.data.mandate_name = this.clients.filter((_) => _.id == res.data.mandate_id)[0].name;

          if (res.data.media_server_account == null) {
            res.data.media_server_account = {
              protocol: null,
              remote_path: null,
              host: null,
              port: null,
              username: null,
              password: null,
            };
          }

          if (res.data.iriis_account == null || res.data.iriis_account === '[]') {
            res.data.iriis_account = {
              protocol: null,
              remote_path: null,
              host: null,
              port: null,
              username: null,
              password: null,
            };
          }

          res.data.bundletypes = res.data.bundletypes.map((_) => _.id);

          // in case kisoft_mode is null, set it to "webservice"
          res.data.kisoft_mode = res.data.kisoft_mode ?? "webservice";

          this.warehouse = res.data;

          if (!this.warehouse.genomix) {
            this.warehouse.genomix = {
              status: false,
              api_key: "",
              mode: "",
              attributes_settings: [],
              downlink: {
                status: false,
                api_keys: [],
              },
            };

            this.prepareAttributes(this.warehouse.genomix.attributes_settings);
            this.prepareGenomixApiKeys(this.warehouse.genomix.downlink.api_keys);
          }

          if (this.warehouse.settings == null || this.warehouse.settings == "[]") {
            this.warehouse._settings = {};
          } else {
            this.warehouse._settings = JSON.parse(res.data.settings);
          }

          if (this.warehouse._settings.bundleCompleteRules) {
            for (let type in this.warehouse._settings.bundleCompleteRules) {
              this.warehouse._settings.bundleCompleteRules[type].forEach((key) => {
                this.bundleCompleteRules.forEach((rule) => {
                  if (rule.name == key) {
                    rule[type].checked = true;
                  }
                });
              });
            }
          }

          if (this.warehouse.symbols) {
            this.$set(this.warehouse, "_symbols", null);
            this.warehouse._symbols = this.warehouse.symbols.map((s) => s.id);
          }

          this.warehouse.productionsystems = this.warehouse.productionsystems.map((_) => _.id);

          this.$set(this.warehouse, "lhmHelper", []);
          this.warehouse.lhmHelper = this.warehouse.loadcarriers.filter((_) => _.type == "pallet").map((_) => _.id);

          this.$set(this.warehouse, "trayHelper", []);
          this.warehouse.trayHelper = this.warehouse.loadcarriers.filter((_) => _.type == "tray");

          this.changeTitle();

          this.isLoading = false;
        });
      });
    },

    prepareAttributes(attributeSettings) {
      const attributes = cloneDeep(this.attributes);
      if (attributeSettings && !attributeSettings.length) return;
      attributeSettings.forEach((attr) => {
        const index = attributes.findIndex((el) => el.id === attr.id);
        if (index === -1) throw new Error("Cannot find an attribute");
        attributes[index] = Object.assign(attributes[index], { ...attr });
      });
      this.attributes = attributes;
    },

    prepareGenomixApiKeys(apiKeys) {
      if (apiKeys && !apiKeys.length) return;
      this.downlinkApiKeys = cloneDeep(apiKeys);
    },

    async getMandatoryData() {
      try {
        const [request1, request2, request3, request4, request5, request6, request7] = await Promise.all([
          this.axios.get("productionsystems"),
          this.axios.get("loadcarriers"),
          this.axios.get("mandates"),
          this.axios.get("multiscans?warehouse_id=" + this.defaultTray.warehouse_id),
          this.axios.get("bundletypes"),
          this.axios.get("teachin/bundleCompleteRules"),
          this.axios.get("attributes"),
        ]);

        this.productionSystems = request1.data;
        this.loadCarriers = request2.data.filter((_) => _.type == "pallet" && _.warehouse_id == null);
        this.clients = request3.data;
        this.multiscans = request4.data;

        // get translated name for the bundle type and save it in "displayName"
        request5.data.forEach((_) => {
          _.displayName = this.$t("bundletypes_" + _.level + "_" + _.name);
        });

        this.bundleTypes = request5.data;

        // build our rules table
        request6.data.forEach((_) => {
          for (const [key, value] of Object.entries(_)) {
            this.bundleCompleteRules.push({
              name: key,
              rule: "BUNDLE_COMPLETE_RULE_" + key.toUpperCase(),
              case: {
                visible: _[key].indexOf("case") > -1,
                checked: false,
              },
              pack: {
                visible: _[key].indexOf("pack") > -1,
                checked: false,
              },
              unit: {
                visible: _[key].indexOf("unit") > -1,
                checked: false,
              },
            });
          }
        });

        this.warehouseSymbols = request7.data
          .filter((_) => _.isSymbol == true)
          .map((s) => {
            return {
              ...s,
              pivot: {
                attribute_id: s.id,
                warehouse_id: parseInt(this.$route.params.id),
              },
            };
          });
      } catch (err) {
        console.error(err);
      }
    },

    changeTitle() {
      this.$route.meta.pageTitle = this.warehouse.name;
      this.$route.meta.mandate = this.warehouse.mandate_name;

      // Add a temporary query parameter.
      this.$router.replace({ query: { temp: Date.now() } });

      // Remove the temporary query parameter.
      this.$router.replace({ query: { temp: undefined } });
    },

    trayDelete(index) {
      this.$confirm(this.$t("GLOBAL-CONFIRMATION-DELETE-MSG"), {
        buttonTrueText: this.$t("GLOBAL-CONFIRMATION-DELETE-YES"),
        buttonFalseText: this.$t("GLOBAL-CONFIRMATION-DELETE-CANCEL"),
      }).then((res) => {
        if (res) {
          this.warehouse.trayHelper.splice(index, 1);
        }
      });
    },

    trayEdit(item, index) {
      this.editedIndex = index;
      this.editedTray = Object.assign({}, item);

      this.dialog.title = this.$t("WAREHOUSE-TRAY-EDIT");
      this.dialog.maxWidth = "640px";
      this.dialog.type = "tray";
      this.dialog.show = true;
    },

    trayNew() {
      this.dialog.title = this.$t("WAREHOUSE-TRAY-ADD");
      this.dialog.maxWidth = "640px";
      this.dialog.type = "tray";
      this.dialog.show = true;
    },

    traySave() {
      if (this.editedIndex > -1) {
        this.warehouse.trayHelper[this.editedIndex] = Object.assign({}, this.editedTray);
      } else {
        this.warehouse.trayHelper.push(this.editedTray);
      }

      this.closeModal();
    },

    scanNew() {
      this.dialog.title = this.$t("WAREHOUSE-MULTISCAN-ADD");
      this.dialog.maxWidth = "520px";
      this.dialog.type = "scan";
      this.dialog.show = true;
    },

    scanEdit(item, index) {
      this.editedIndex = index;
      this.editedScan = Object.assign({}, item);

      this.dialog.title = this.$t("WAREHOUSE-MULTISCAN-EDIT");
      this.dialog.maxWidth = "520px";
      this.dialog.type = "scan";
      this.dialog.show = true;
    },

    scanDelete(index) {
      this.$confirm(this.$t("GLOBAL-CONFIRMATION-DELETE-MSG"), {
        title: this.$t("GLOBAL-CONFIRMATION-DELETE-TITLE"),
        buttonTrueText: this.$t("GLOBAL-CONFIRMATION-DELETE-YES"),
        buttonFalseText: this.$t("GLOBAL-CONFIRMATION-DELETE-CANCEL"),
      }).then((res) => {
        if (res) {
          this.axios.delete("multiscans/" + this.multiscans[index].id).then((res) => {
            this.multiscans.splice(index, 1);
          });
        }
      });
    },

    scanSave() {
      if (this.editedIndex > -1) {
        this.axios.put("multiscans/" + this.editedScan.id, this.editedScan).then((res) => {
          this.multiscans[this.editedIndex] = Object.assign({}, this.editedScan);
          this.closeModal();
        });
      } else {
        this.axios.post("multiscans", this.editedScan).then((res) => {
          this.multiscans.push(res.data);
          this.closeModal();
        });
      }
    },

    save() {
      this.isSaving = true;

      this.warehouse.loadcarriers = this.warehouse.lhmHelper;

      this.warehouse.symbols = [];
      this.warehouse._symbols.forEach((s) => {
        this.warehouse.symbols.push(this.warehouseSymbols.find((ws) => ws.id == s));
      });

      if (this.warehouse.trayHelper.length > 0) {
        let traysReadyToSave = this.warehouse.trayHelper.filter((_) => typeof _.id != "undefined");
        let traysToDo = this.warehouse.trayHelper.filter((_) => typeof _.id == "undefined");

        let promises = [];

        for (const tray of this.warehouse.trayHelper) {
          if (typeof tray.id == "undefined") {
            promises.push(this.axios.post("loadcarriers", tray));
          } else {
            promises.push(this.axios.put("loadcarriers/" + tray.id, tray));
          }
        }

        // make the request(s)
        Promise.all(promises)
          .then((resp) => {
            let trayIds = [];

            // get ids of the updated/posted trays and add it to
            for (var key in resp) {
              trayIds.push(resp[key].data.id);
            }

            // merge the new trayIds with the existing loadcarriers ids and save it in warehouse.loadcarriers
            this.warehouse.loadcarriers = this.warehouse.loadcarriers.concat(trayIds);

            this._save();
          })
          .catch((err) => {
            this.isSaving = false;
            this.$store.dispatch("setSnackbar", {
              text: "ERROR: ".err,
              type: "error",
            });
            console.error(err);
          });
      } else {
        this._save();
      }
    },

    _save() {
      this.warehouse._settings.bundleCompleteRules = {
        case: [],
        pack: [],
        unit: [],
      };

      ["case", "pack", "unit"].forEach((type) => {
        this.warehouse._settings.bundleCompleteRules[type] = this.bundleCompleteRules
          .filter((b) => b[type]["visible"] == true && b[type]["checked"] == true)
          .map((b) => b.name);
      });
      if(this.isTeachinChanged){
        const settings = {};
        const globalSettings = this.teachinGlobalSetting === null? []: this.teachinGlobalSetting;
        if(globalSettings && globalSettings.length > 0){
          for(const el of globalSettings){
            settings[el.name] = el.value;
          }
        }
        const sections = [];
        // const sections = {};
        const teachinSections = this.teachInSections === null? []: this.teachInSections;
        if(teachinSections && teachinSections.length>0){
          for(const el of teachinSections){
            // const steps = {};
            const steps = [];
            if(el.steps && el.steps.length > 0){
              for(const step of el.steps){
                const settings = {};
                if(step.settings && step.settings.length > 0){
                  for(const setting of step.settings){
                    settings[setting.name] = setting.value === undefined? false: setting.value;
                  }
                }
                
                steps.push({
                  name: step.name,
                  active: step.active,
                  settings
                });
              }
            }
            sections.push({
              name: el.name,
              active: el.active,
              steps
            });
          }
        }
        this.warehouse._settings.teachin = {sections, settings};
      }
      this.warehouse.settings = JSON.stringify(this.warehouse._settings);
      
      this.axios
        .put("warehouses/" + this.warehouse.id, this.warehouse)
        .then((res) => {
          this.$store.dispatch("setSnackbar", {
            text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED"),
          });
          this.isSaving = false;
          this.closeModal();
        })
        .catch((err) => {
          this.$store.dispatch("setSnackbar", {
            text: this.$t("GLOBAL-MESSAGE-ERROR"),
            type: "error",
          });
          this.isSaving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "~vuetify/src/styles/styles.sass";

.trays {
  &__row {
    &:not(:last-of-type) {
      border-bottom: 1px solid #cdcdcd;
    }
  }
}

.bundle-complete-tbl {
  border: 1px solid #cdcdcd;
  &.v-data-table::v-deep > table > td {
    border-bottom: 1px solid #cdcdcd !important;
  }
}

.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row),
.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > th:not(.v-data-table__mobile-row) {
  border-bottom: 1px solid #cdcdcd !important;
}

.teachInGroup {
  .teachInGroupTitle {
    font-size: 18px;
  }

  .teachInStep {
    min-height: 185px;
    position: relative;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.3s ease;

    &:hover {
      background-color: #f0f0f0 !important;
      border: 1px solid #aaa !important;
    }

    .teachInStepTitle {
      display: block;
    }

    .overlay {
      position: absolute;
      display: none;
      justify-content: center;
      align-items: center;
      z-index: 999;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.4);
      /* should use var primary-color here! */
      border: 1px solid #ffed00;

      i {
        font-size: 68px !important;
      }
    }

    &.is-active {
      /* should use var primary-color here! */
      background-color: #ffed00 !important;

      .overlay {
        display: flex;
      }
    }
  }
}
</style>
